import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ChevronRight, Clock3, Trophy, Wallet } from "lucide-react";
import { otherBalancesHint } from "@/features/panel/otherBalances";
import {
  COMMISSION_ROUTES,
  CUSTOMER_TYPE_LABELS,
  commissionSummaryResponseSchema,
  formatPrice,
  splitTimeLeft,
} from "@nh/shared";
import { authFetch } from "@/shared/api/session";
import { Button } from "@/shared/ui/core";
import { CardHeader } from "./CardHeader";
import { nextCommissionCutoff } from "./cutoff";

/**
 * ═══════════════════════════════════════════════════════════════════
 * Las tres cards de al lado del paquete — réplica del resumen de
 * producción (`DashboardSocio.tsx`): comisiones disponibles, tu nivel y
 * el reloj de comisiones. Mismos endpoints que sus secciones.
 * ═══════════════════════════════════════════════════════════════════
 */

export function useMyCommissionSummary() {
  return useQuery({
    queryKey: ["me", "commissions", "summary"],
    queryFn: async () => {
      const data = await authFetch(COMMISSION_ROUTES.summary, commissionSummaryResponseSchema, {
        baseUrl: "",
      });
      return data.summary;
    },
  });
}

/**
 * "COMISIONES DISPONIBLES". En producción es `pockets.released`; aquí el
 * libro tiene `pending` y `paid`, y lo devengado que aún no se paga es
 * `pending`. El botón "Solicitar retiro" está DESHABILITADO a propósito:
 * la solicitud de retiro del socio es el anclaje (3) del motor y no
 * existe todavía. Un botón que no hace nada sin decirlo sería peor.
 */
export function AvailableCommissionsCard({ basePath }: { basePath: "/socio" | "/mi-negocio" }) {
  const { t } = useTranslation();
  const summary = useMyCommissionSummary();

  return (
    <section
      aria-label={t("Comisiones disponibles")}
      className="rounded-3xl border border-ds-border bg-white p-5 shadow-ds-float"
    >
      <CardHeader icon={Wallet} item="wallet" title={t("Comisiones disponibles")} to={`${basePath}/comisiones`} />
      <p className="mt-4 font-mono text-[40px] font-semibold leading-none text-ds-ink">
        {summary.data ? formatPrice(summary.data.pendingCents, summary.data.currency) : "—"}
      </p>
      <p className="mt-1.5 text-[13px] text-ds-ink-3">
        {summary.data?.currency === "USD"
          ? t("Dólares · devengado, por liberar")
          : t("Pesos mexicanos · devengado, por liberar")}
        {/* Y lo que tenga en la OTRA moneda, aparte: no se suma (14-sep). */}
        {otherBalancesHint(summary.data, "pendingCents") && (
          <span className="ml-1.5 font-medium text-ds-ink-2">
            {otherBalancesHint(summary.data, "pendingCents")}
          </span>
        )}
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button leftIcon={<Wallet />} disabled title={t("Los retiros se habilitan con la liberación de comisiones")}>
          {t("Solicitar retiro")}
        </Button>
        <span className="text-[12px] text-ds-ink-3">
          {t("Los retiros se habilitan con la liberación de comisiones.")}
        </span>
      </div>
    </section>
  );
}

/** "TU NIVEL": el rango (hoy todos en I) y la categoría de la red. */
export function RankCard({ basePath }: { basePath: "/socio" | "/mi-negocio" }) {
  const { t } = useTranslation();
  const summary = useMyCommissionSummary();
  const rank = summary.data?.rankId ?? "I";
  const category = summary.data ? CUSTOMER_TYPE_LABELS[summary.data.customerType] : null;

  return (
    <section aria-label={t("Tu nivel")} className="rounded-3xl border border-ds-border bg-white p-5 shadow-ds-float">
      <CardHeader icon={Trophy} item="copa" title={t("Tu nivel")} to={`${basePath}/comisiones`} />
      <div className="mt-4 flex items-center gap-5">
        <span
          className="flex h-24 w-24 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#A99BFF] to-ds-violet-pop font-serif text-[34px] italic text-white shadow-[0_18px_40px_rgba(92,79,214,0.35)]"
          aria-label={t("Rango {{rank}}", { rank })}
        >
          {rank}
        </span>
        <div className="min-w-0">
          <p className="text-[11px] font-bold uppercase tracking-ds-label text-ds-ink-3">{t("Categoría")}</p>
          <p className="mt-0.5 truncate text-[20px] font-semibold text-ds-ink">
            {category ? t(category) : "—"}
          </p>
          <Link
            to={`${basePath}/comisiones`}
            className="mt-1 inline-flex items-center gap-0.5 text-[13.5px] font-medium text-ds-violet-pop hover:underline"
          >
            {t("Ver progreso")}
            <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

/**
 * "RELOJ DE COMISIONES": cuánto falta para el corte del periodo (fin de
 * mes) y qué tanto del mes ya corrió. Es aritmética de calendario —el
 * corte mensual como PROCESO es el anclaje (1) del motor y aún no corre.
 */
export function CommissionClockCard() {
  const { t, i18n } = useTranslation();
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(id);
  }, []);

  const { cutoff, progress } = nextCommissionCutoff(now);
  const left = splitTimeLeft(cutoff, now);
  const cutoffLabel = new Intl.DateTimeFormat(i18n.language === "en" ? "en-US" : "es-MX", {
    day: "numeric",
    month: "long",
  }).format(cutoff);

  return (
    <section
      aria-label={t("Reloj de comisiones")}
      className="rounded-3xl border border-ds-border bg-white p-5 shadow-ds-float"
    >
      <CardHeader icon={Clock3} item="reloj" title={t("Reloj de comisiones")} />
      <p className="mt-4 flex items-baseline gap-x-2 font-mono text-ds-ink" data-testid="cutoff">
        <span className="text-[40px] font-semibold leading-none">{left.days}</span>
        <span className="text-[13px] text-ds-ink-3">{t("días")}</span>
        <span className="ml-1 text-[28px] font-semibold leading-none">{left.hours}</span>
        <span className="text-[13px] text-ds-ink-3">h</span>
      </p>
      <p className="mt-1.5 text-[13px] text-ds-ink-3">{t("Próximo corte: {{date}}", { date: cutoffLabel })}</p>
      <div
        className="mt-4 h-2 w-full overflow-hidden rounded-full bg-ds-lavender-bg"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(progress * 100)}
        aria-label={t("Avance del periodo")}
      >
        <div
          className="h-full rounded-full bg-gradient-to-r from-ds-violet-pop to-ds-violet-deep transition-[width]"
          style={{ width: `${Math.round(progress * 100)}%` }}
        />
      </div>
    </section>
  );
}
